import { DivConst } from 'constants/divConst';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { CompanyMemberSearchItemType } from 'types/api/companyMemberSearchItemType';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

export type UseSettingMemberResult = {
  initialized: boolean;
  processing: boolean;
  membersParticipatedState: CompanyMemberSearchItemType[];
  membersInvitedState: CompanyMemberSearchItemType[];
  setMembersInvitedState: (members: CompanyMemberSearchItemType[]) => void;
  invitationModalOpen: boolean;
  setInvitationModalOpen: (open: boolean) => void;
  isAdmin: boolean;
  onInvite: () => void;
  onEdit: (item: CompanyMemberSearchItemType) => void;
  onInvitationResend: (item: CompanyMemberSearchItemType) => void;
  onInvitationDelete: (item: CompanyMemberSearchItemType) => void;
};

// ------------------------------------------------------------------ //
// hook
// ------------------------------------------------------------------ //

/**
 * カンパニーメンバー設定画面のロジックを集約するカスタムフック。
 */
export function useSettingMember(): UseSettingMemberResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [acctState] = useAtom(AcctState);

  const { noticeMessageSuccess, confirm } = useAppState();
  const { postCompanyMemberSearch, postInvitationResend, postInvitationDelete } = useApiState();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [membersParticipatedState, setMembersParticipatedState] = useState<
    CompanyMemberSearchItemType[]
  >([]);
  const [membersInvitedState, setMembersInvitedState] = useState<CompanyMemberSearchItemType[]>([]);
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        const response = await postCompanyMemberSearch({
          companyId: acctState.company.id,
        });

        // 参加中メンバー一覧
        setMembersParticipatedState(
          response.accounts.filter(
            item =>
              item.member.companyMemberStatusDiv ===
              DivConst.items.companyMemberStatus.participated.value
          )
        );

        // 招待中メンバー一覧
        setMembersInvitedState(
          response.accounts.filter(
            item =>
              item.member.companyMemberStatusDiv ===
              DivConst.items.companyMemberStatus.invited.value
          )
        );
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 初回マウント時のみ実行する設計
  }, []);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 管理権限を持っているかどうかを判定
   */
  const _isAdmin = (): boolean => {
    return acctState.company.member?.roleDiv === DivConst.items.role.admin.value;
  };

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * ユーザー追加ボタン押下時の処理
   */
  const onInvite = () => {
    setInvitationModalOpen(true);
  };

  /**
   * 編集ボタン押下時の処理
   */
  const onEdit = (item: CompanyMemberSearchItemType) => {
    navigate(`/setting/${acctState.company.cd}/member/edit`, {
      state: {
        companyMemberId: item.member.id,
        userName: item.user?.name ?? '',
        userMailAddress: item.user?.mailAddress ?? '',
        roleDiv: item.member?.roleDiv,
      },
    });
  };

  /**
   * 招待再送信ボタン押下時の処理
   */
  const onInvitationResend = (item: CompanyMemberSearchItemType) => {
    confirm({
      title: '招待の再送信',
      message: `「${item.member.invitationMailAddress}」に招待メールを再送信よろしいですか？`,
      onSubmit: async () => {
        try {
          setProcessing(true);

          await postInvitationResend({
            companyMemberId: item.member.id,
          });
          noticeMessageSuccess('招待メールの再送信が完了しました。');
        } catch (e) {
          showBoundary(e);
        } finally {
          setProcessing(false);
        }
      },
    });
  };

  /**
   * 招待取消ボタン押下時の処理
   */
  const onInvitationDelete = (member: CompanyMemberSearchItemType) => {
    confirm({
      title: '招待の取り消し',
      message: `「${member.member.invitationMailAddress}」を招待を取り消してもよろしいですか？`,
      onSubmit: async () => {
        try {
          setProcessing(true);

          await postInvitationDelete({
            companyMemberId: member.member.id,
          });
          noticeMessageSuccess('招待の取り消しが完了しました。');
          setMembersInvitedState(prev => prev.filter(item => item.member.id !== member.member.id));
        } catch (e) {
          showBoundary(e);
        } finally {
          setProcessing(false);
        }
      },
    });
  };

  return {
    initialized,
    processing,
    membersParticipatedState,
    membersInvitedState,
    setMembersInvitedState,
    invitationModalOpen,
    setInvitationModalOpen,
    isAdmin: _isAdmin(),
    onInvite,
    onEdit,
    onInvitationResend,
    onInvitationDelete,
  };
}
